<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-6 overflow-auto text-left">
    <SupplierModal
      :isOpen="showModal"
      :editSupplier="editingSupplier"
      @close="closeModal"
      @save="createSupplier"
      @update="updateSupplier"
    />

    <!-- Table -->
    <div class="w-full overflow-x-auto overflow-y-auto max-h-[calc(100vh-250px)]">
      <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
        <thead>
          <tr>
            <th>No.</th>
            <th @click="sortBy('name')" class="sort-col">
              Name <span v-if="sortKey === 'name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('remark')" class="sort-col">
              Remark <span v-if="sortKey === 'remark'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('created_at')" class="sort-col">
              Created At <span v-if="sortKey === 'created_at'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(sup, index) in sortedSuppliers" :key="sup.id" class="hover:bg-slate-100/50 transition-colors border-b">
            <td>{{ index + 1 }}</td>
            <td>{{ sup.name }}</td>
            <td>{{ sup.remark || '-' }}</td>
            <td>{{ formatDate(sup.created_at) }}</td>
            <td>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  @click="editSupplierData(sup)"
                  class=" editbtn text-emerald-600"
                  :class="canEditSupplier ? 'hover:underline' : 'opacity-50 cursor-not-allowed action-disabled-marker'"
                  :disabled="!canEditSupplier"
                >Edit</button>

                <label
                  class="toggle-switch"
                  :class="[
                    sup.__togglingStatus ? 'opacity-50 pointer-events-none' : '',
                    !canEditSupplier ? 'opacity-50 cursor-not-allowed action-disabled-marker' : ''
                  ]"
                  title="Toggle status"
                >
                  <input
                    type="checkbox"
                    :checked="(sup.status ?? 1) === 1"
                    :disabled="!canEditSupplier"
                    @change="toggleSupplierStatus(sup)"
                  >
                  <span class="slider"></span>
                </label>
              </div>
              <!-- <button
                type="button"
                @click="deleteSupplier(sup.id)"
                class="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded ml-2"
                title="Delete Supplier"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3">
                  <path fill-rule="evenodd" d="M8.75 3A2.75 2.75 0 0 0 6 5.75v.5H4.5a.75.75 0 0 0 0 1.5h11a.75.75 0 0 0 0-1.5H14v-.5A2.75 2.75 0 0 0 11.25 3h-2.5ZM12.5 6.25v-.5a1.25 1.25 0 0 0-1.25-1.25h-2.5A1.25 1.25 0 0 0 7.5 5.75v.5h5Z" clip-rule="evenodd" />
                  <path d="M4.5 9.75a.75.75 0 0 1 .75-.75h9.5a.75.75 0 0 1 .75.75v6.75A2.75 2.75 0 0 1 12.75 19.25H7.25A2.75 2.75 0 0 1 4.5 16.5V9.75Z" />
                </svg>
              </button> -->
            </td>
          </tr>
          <tr v-if="loading">
  <td colspan="9" class="text-center py-10">
    <div class="flex items-center justify-center">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </td>
</tr>
          <tr v-else-if="error">
            <td colspan="5" class="text-center py-10 text-red-500">{{ error }}</td>
          </tr>
          <tr v-else-if="suppliers.length === 0">
            <td colspan="5" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
              No suppliers found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import store from '../../store'
import { apiFetch, WS_BASE_URL } from '../../utils/api'
import SupplierModal from './SupplierModal.vue'

const suppliers = ref([])
const loading = ref(false)
const error = ref('')
const sortKey = ref('name')
const sortOrder = ref('asc')

const showModal = ref(false)
const editingSupplier = ref(null)

let supplierSyncSocket = null
let supplierSyncReconnectTimer = null

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
const supplierPrivilege = computed(() => userPrivileges.value?.Supplier || userPrivileges.value?.supplier || null)
const canAddSupplier = computed(() => hasAdminPrivilege.value || !!supplierPrivilege.value?.Add)
const canEditSupplier = computed(() => hasAdminPrivilege.value || !!supplierPrivilege.value?.Edit)

const sortedSuppliers = computed(() => {
  const result = [...suppliers.value]
  result.sort((a, b) => {
    const modifier = sortOrder.value === 'asc' ? 1 : -1
    const valA = (a[sortKey.value] ?? '').toString().toLowerCase()
    const valB = (b[sortKey.value] ?? '').toString().toLowerCase()
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })
  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function formatDate(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toISOString().slice(0, 10)
}

async function loadSuppliers() {
  error.value = ''
  if (!companyId.value && !branchId.value) {
    error.value = 'Context (Company or Branch) not found in user profile.'
    return
  }

  loading.value = true
  try {
    let endpoint = `/suppliers/company/${companyId.value}`
    if (branchId.value) {
      endpoint = `/suppliers/branch/${branchId.value}`
    }
    
    const response = await apiFetch(endpoint, { method: 'GET' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to load suppliers')
    }
    const data = await response.json()
    suppliers.value = Array.isArray(data) ? data : []
  } catch (err) {
    error.value = err.message || 'Failed to load suppliers'
  } finally {
    loading.value = false
  }
}

function openAddModal() {
  if (!canAddSupplier.value) return
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editingSupplier.value = null
}

function editSupplierData(sup) {
  if (!canEditSupplier.value) return
  editingSupplier.value = sup
  showModal.value = true
}

async function createSupplier(payload) {
  const response = await apiFetch('/suppliers', {
    method: 'POST',
    body: JSON.stringify({
      companyId: branchId.value ? null : companyId.value,
      branchId: branchId.value || null,
      name: payload.name,
      remark: payload.remark,
      created_by: userId.value,
    }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'Failed to create supplier')
  }

  await loadSuppliers()
}

async function updateSupplier(payload) {
  const response = await apiFetch(`/suppliers/${payload.id}`, {
    method: 'PUT',
    body: JSON.stringify({
      name: payload.name,
      remark: payload.remark,
      updated_by: userId.value,
    }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'Failed to update supplier')
  }

  await loadSuppliers()
}

async function toggleSupplierStatus(sup) {
  if (!canEditSupplier.value) return
  if (!sup?.id || sup.__togglingStatus) return
  sup.__togglingStatus = true
  const current = (sup.status ?? 1) === 1 ? 1 : 0
  const next = current === 1 ? 0 : 1
  try {
    const res = await apiFetch(`/suppliers/${sup.id}/toggle-status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: next })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to toggle supplier status')
    }
    sup.status = next
  } catch (e) {
    alert(e.message || 'Failed to toggle supplier status')
  } finally {
    sup.__togglingStatus = false
  }
}

// async function deleteSupplier(id) {
//   if (!confirm('Are you sure you want to delete this supplier?')) return

//   const response = await apiFetch(`/suppliers/${id}`, {
//     method: 'DELETE',
//   })

//   if (!response.ok) {
//     const data = await response.json().catch(() => ({}))
//     throw new Error(data.message || 'Failed to delete supplier')
//   }

//   await loadSuppliers()
// }

function onOpenSupplierModal() {
  if (!canAddSupplier.value) return
  openAddModal()
}

function disconnectSupplierSyncRealtime() {
  if (supplierSyncReconnectTimer) {
    clearTimeout(supplierSyncReconnectTimer)
    supplierSyncReconnectTimer = null
  }
  if (supplierSyncSocket) {
    try {
      supplierSyncSocket.close()
    } catch (_) {
      // ignore close failures
    }
    supplierSyncSocket = null
  }
}

function connectSupplierSyncRealtime() {
  disconnectSupplierSyncRealtime()

  const user = currentUser.value
  if (!user || typeof WebSocket === 'undefined') return

  const scopedCompanyId = user.companyId || user.company_id || user.company?.id
  if (!scopedCompanyId) return

  const scopedBranchId = user.branchId || user.branch_id || user.branch?.id || null
  const socket = new WebSocket(WS_BASE_URL)
  supplierSyncSocket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({
      type: 'frontend.subscribe',
      company_id: scopedCompanyId,
      branch_id: scopedBranchId,
    }))
  })

  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.event?.startsWith('supplier.')) {
        loadSuppliers()
      }
    } catch (_) {
      // ignore malformed realtime payloads
    }
  })

  socket.addEventListener('close', () => {
    supplierSyncSocket = null
    supplierSyncReconnectTimer = setTimeout(() => {
      connectSupplierSyncRealtime()
    }, 3000)
  })
}

onMounted(async () => {
  await loadSuppliers()
  connectSupplierSyncRealtime()
  window.addEventListener('open-supplier-modal', onOpenSupplierModal)
})

onUnmounted(() => {
  disconnectSupplierSyncRealtime()
  window.removeEventListener('open-supplier-modal', onOpenSupplierModal)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  color: #1e293b;
}

th,
td {
  padding: 5px 6px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

th.sort-col {
  cursor: pointer;
}
th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f1f5f9;
}

/* Toggle Switch (same style as departmentPage.vue / usersPage.vue) */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}

.action-disabled-marker {
  position: relative;
}

.action-disabled-marker:hover::after {
  content: '⊘';
  position: absolute;
  top: -8px;
  right: -10px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1;
  pointer-events: none;
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}
</style>
